/**
 * Print Page layout for the State Report notice.
 * Portrait + margins keeps the sheet right-side-up; collapsing the app
 * chrome keeps a leftover sliver from becoming a blank second page.
 */
import { getStateReportPreviewTitle } from "./noClientActionRequired";

/** Save-as-PDF uses the tab title; swap it so the file is not named MISC. */
export function getStateReportPrintFileName(
  additionalNotes?: string | null,
  status?: string | null,
): string {
  return getStateReportPreviewTitle(additionalNotes, status);
}

export function beginStateReportPrintDocumentTitle(
  additionalNotes?: string | null,
  status?: string | null,
): () => void {
  const previousTitle = document.title;
  document.title = getStateReportPrintFileName(additionalNotes, status);
  let restored = false;
  return () => {
    if (restored) return;
    restored = true;
    document.title = previousTitle;
  };
}
export const STATE_REPORT_PRINT_PAGE_RULE = {
  size: "letter portrait",
  margin: "0.5in",
} as const;

const LETTER_HEIGHT_IN = 11;

/** Printable height after @page top + bottom margin. */
export function getStateReportPrintableHeight(
  pageHeightIn = LETTER_HEIGHT_IN,
  margin: string = STATE_REPORT_PRINT_PAGE_RULE.margin,
): string {
  const marginIn = Number.parseFloat(margin);
  const usable = pageHeightIn - 2 * marginIn;
  return `${usable > 0 ? usable : pageHeightIn}in`;
}

export const STATE_REPORT_PRINT_SCALE_VAR = "--sr-print-scale";

const PRINT_SCALE = {
  base: 1.08,
  min: 0.8,
  max: 1.18,
  extraDocumentWeight: 0.04,
  extraCharacterWeight: 1 / 900,
  bodySoftCap: 350,
} as const;

export type StateReportPrintScaleInput = {
  documentCount: number;
  bodyTextLength: number;
};

/**
 * More attachments / longer notes shrink type so the notice stays on one
 * sheet. A short notice grows type so the page fills without hollow cards.
 */
export function getStateReportPrintScale({
  documentCount,
  bodyTextLength,
}: StateReportPrintScaleInput): number {
  const extraDocs = Math.max(0, Number(documentCount) || 0);
  const extraChars = Math.max(0, (Number(bodyTextLength) || 0) - PRINT_SCALE.bodySoftCap);
  const raw =
    PRINT_SCALE.base -
    extraDocs * PRINT_SCALE.extraDocumentWeight -
    extraChars * PRINT_SCALE.extraCharacterWeight;
  const clamped = Math.min(PRINT_SCALE.max, Math.max(PRINT_SCALE.min, raw));
  return Math.round(clamped * 100) / 100;
}

function printCalc(base: string): string {
  return `calc(${base} * var(${STATE_REPORT_PRINT_SCALE_VAR}, 1))`;
}

function printSize(base: string): string {
  return `${printCalc(base)} !important`;
}

/**
 * A letter sheet is narrower than the MUI `md` breakpoint (900px), so every
 * `md` column in the notice would stack and double the page height. Pin the
 * widths the layout was designed with — Status stays on one line, and the
 * notice fits a single sheet.
 */
const mdColumnWidth: Record<number, string> = {
  4: "33.333333%",
  5: "41.666667%",
  6: "50%",
  7: "58.333333%",
};

export const STATE_REPORT_PRINT_GRID_RULES = Object.fromEntries(
  Object.entries(mdColumnWidth).map(([columns, width]) => [
    `.print-area .MuiGrid-grid-md-${columns}`,
    {
      flexBasis: `${width} !important`,
      maxWidth: `${width} !important`,
      flexGrow: "0 !important",
    },
  ]),
);

export const STATE_REPORT_PRINT_FIT_RULES = {
  "#root": {
    display: "none !important",
    height: "0 !important",
    overflow: "hidden !important",
  },
  "html, body": {
    height: "auto !important",
    overflow: "visible !important",
    margin: "0 !important",
    padding: "0 !important",
    background: "#ffffff !important",
  },
  ".MuiBackdrop-root": {
    display: "none !important",
  },
  ".MuiDialog-root": {
    position: "absolute !important",
    left: "0 !important",
    top: "0 !important",
    height: "auto !important",
    maxHeight: "none !important",
  },
  ".MuiDialog-container": {
    display: "block !important",
    height: "auto !important",
    minHeight: "0 !important",
    overflow: "visible !important",
  },
  ".MuiDialog-paper": {
    minHeight: "0 !important",
    height: "auto !important",
    maxHeight: "none !important",
    overflow: "visible !important",
    display: "block !important",
    boxSizing: "border-box !important",
  },
  ".print-wrapper.single-page": {
    overflow: "visible !important",
    maxHeight: "none !important",
    height: "auto !important",
    pageBreakAfter: "auto !important",
  },
  // Keep the attachments heading with its table rather than orphaning it.
  ".print-documents-section": {
    pageBreakInside: "avoid !important",
    breakInside: "avoid !important",
  },
  // Screen DataGrid stays on screen only — print uses the HTML table beside it.
  ".print-documents-section .print-document-table": {
    display: "none !important",
    visibility: "hidden !important",
    height: "0 !important",
    overflow: "hidden !important",
  },
  ".print-status-section": {
    pageBreakInside: "avoid !important",
    breakInside: "avoid !important",
    pageBreakBefore: "avoid !important",
  },
} as const;

/**
 * Report Summary is six independent flex cells. Long values wrap and labels
 * have different widths, so left/right rows drift off the same baseline.
 * A 2×2 grid keeps each pair on one line and lines the values up.
 */
export const STATE_REPORT_PRINT_SUMMARY_RULES = {
  ".print-report-summary-fields": {
    display: "grid !important",
    gridTemplateColumns: "1fr 1fr !important",
    columnGap: "12px !important",
    rowGap: "2px !important",
    width: "100% !important",
  },
  ".print-report-summary-fields > .MuiGrid-item": {
    maxWidth: "100% !important",
    flexBasis: "auto !important",
    width: "auto !important",
    paddingTop: "2px !important",
    paddingBottom: "2px !important",
  },
  ".print-report-summary-row": {
    display: "grid !important",
    gridTemplateColumns: "1.2in 1fr !important",
    alignItems: "start !important",
    columnGap: "8px !important",
    width: "100% !important",
    paddingTop: "2px !important",
    paddingBottom: "2px !important",
  },
  ".print-report-summary-row .MuiTypography-root": {
    minWidth: "0 !important",
  },
  ".print-report-summary-row .MuiTypography-root:last-of-type": {
    textAlign: "left !important",
  },
} as const;

/**
 * Keep the notice in normal document flow. Stretching to 10in plus the
 * title bar overflows the letter sheet and dumps Status onto page 2.
 */
export const STATE_REPORT_PRINT_FILL_RULES = {
  ".MuiDialog-paper > .MuiDialogTitle-root": {
    flex: "0 0 auto !important",
  },
  ".MuiDialog-paper > .MuiDialogContent-root": {
    display: "block !important",
    overflow: "visible !important",
    padding: "0 !important",
    height: "auto !important",
  },
  ".print-wrapper": {
    display: "block !important",
    height: "auto !important",
    minHeight: "0 !important",
  },
  ".print-area > .MuiGrid-container": {
    alignContent: "flex-start !important",
    alignItems: "stretch !important",
    rowGap: printSize("8px"),
    height: "auto !important",
    minHeight: "0 !important",
  },
  ".print-area > .MuiGrid-container > .MuiGrid-item": {
    display: "flex !important",
    flexDirection: "column !important",
  },
} as const;

/**
 * Screen spacing and type sizes are tuned for a 1200px dialog; on a letter
 * sheet they spill onto a second page. These are the print-only sizes that
 * keep the whole notice on one sheet while staying readable.
 */
export const STATE_REPORT_PRINT_COMPACT_RULES = {
  // Selectors stay more specific than the colour/layout print rules they sit
  // beside, so sizing wins without replacing those rules.
  ".MuiDialog-paper .MuiDialogTitle-root": {
    padding: `${printCalc("6px")} 10px !important`,
    fontSize: printSize("12pt"),
  },
  ".print-wrapper .print-area": {
    fontSize: printSize("9pt"),
    lineHeight: "1.25 !important",
    flex: "1 1 auto !important",
    display: "flex !important",
    flexDirection: "column !important",
    minHeight: "0 !important",
    height: "auto !important",
    padding: `${printCalc("10px")} ${printCalc("10px")} ${printCalc("6px")} !important`,
    boxSizing: "border-box !important",
  },
  ".print-area .MuiTypography-root": {
    fontSize: printSize("9pt"),
    lineHeight: "1.3 !important",
  },
  ".print-area .MuiTypography-overline": {
    fontSize: printSize("8pt"),
    lineHeight: "1.2 !important",
    marginBottom: printSize("4px"),
  },
  ".print-area .MuiTypography-caption": {
    fontSize: printSize("7.5pt"),
    whiteSpace: "nowrap !important",
    marginBottom: "0 !important",
  },
  ".print-wrapper .print-area strong": {
    fontSize: printSize("9.5pt"),
  },
  ".print-area .MuiPaper-root:not(.print-document-table)": {
    padding: printSize("8px"),
    flex: "0 0 auto !important",
    display: "flex !important",
    flexDirection: "column !important",
    justifyContent: "flex-start !important",
    boxSizing: "border-box !important",
  },
  ".print-area .MuiGrid-item": {
    paddingTop: printSize("4px"),
    paddingBottom: "0 !important",
  },
  ".print-area .MuiBox-root": {
    minHeight: "0 !important",
  },
  ".print-documents-section .print-only-document-table table": {
    fontSize: printSize("8.5pt"),
  },
  ".print-documents-section .print-only-document-table th, .print-documents-section .print-only-document-table td": {
    padding: printSize("4px"),
    lineHeight: "1.25 !important",
  },
} as const;
